import { useEffect } from 'react';
import { Box, Button, Modal, Paper } from '@mui/material';
import useTicTacToeModalStyles from './useTicTacToeModalStyles';
import TicTacToeButton from './ticTacToeButton';
import useGameModal from '../../../../hooks/useGameModal';
import useGlobalStyles from '../../../useGlobalStyles';
import { useAppDispatch } from '../../../../redux/hooks';
import { addOneToScore, updateUserFields } from '../../../../redux/slices/userSlice';

/**
 * Interface representing the props for the Tic Tac Toe Modal component.
 *
 * - open Boolean representing whether or not the modal is open.
 * - closeModal The function to close the modal.
 */
interface TicTacToeModalProps {
  open: boolean;
  closeModal: () => void;
}

/**
 * Tic Tac Toe Modal component renders a tic tac toe game board.
 *
 * @param open Boolean representing whether or not the modal is open.
 * @param closeModal The function to close the modal.
 */
const TicTacToeModal = ({ open, closeModal }: TicTacToeModalProps) => {
  const { classes } = useTicTacToeModalStyles();
  const { classes: globalClasses } = useGlobalStyles();
  const dispatch = useAppDispatch();
  const {
    selfId,
    opponent,
    gameStatus,
    gameResult,
    gameType,
    isChallenge,
    oppId,
    challenger,
    canPlay,
    gameOver,
    statusText,
    gameOverText,
    collectPoint,
    hideBoard,
    handleExit,
  } = useGameModal();

  useEffect(() => {
    if (gameOver && gameResult[0] === 'won' && isChallenge && gameType === 'tictactoe') {
      if (gameResult[0] === 'won' && isChallenge) {
        dispatch(addOneToScore());
        dispatch(updateUserFields({ uid: selfId, field: 'score', updatedValue: '1' }));
      }
    }
  }, [dispatch, gameOver, gameResult, gameType, isChallenge, selfId]);

  /**
   * Function to create a Tic Tac Toe Button, many of which make up the game board.
   *
   * @param num - The number of the button being created, which is stored in the key attribute
   * and also helps determine the coordinates of the button on the board.
   * @returns the Tic Tac Toe Button.
   */
  const createButton = (num: number) => (
    <TicTacToeButton
      key={num}
      idx={num}
      selfId={selfId}
      oppId={oppId}
      gameStatus={gameStatus}
      challenger={challenger}
      canPlay={canPlay}
    />
  );

  return (
    <Modal open={open} onClose={() => {}}>
      <Box className={classes.modalStyles} component={Paper}>
        {!hideBoard ? (
          <>
            <span className={`${globalClasses.h3} ${classes.titleText}`}>Tic Tac Toe</span>
            <span className={classes.playingAgainst}>
              Playing against: <span style={{ fontWeight: 600 }}>{opponent?.username || ''}</span>
            </span>
            <div className={classes.selfContainer}>
              <span className={classes.youAre}>You are:</span>
              <div
                className={`${challenger && classes.challengerIcon} ${!challenger && classes.challengedIcon}`}
              />
            </div>
            <span className={classes.status}>{statusText}</span>
            <div className={classes.buttonRows}>
              <div className={`${!canPlay && classes.overlay}`}></div>
              {[...Array(3).keys()]
                .map(i => i * 3)
                .map(row => (
                  <div className={classes.buttonColumns} key={row}>
                    {[...Array(3).keys()].map(i => i + row).map(num => createButton(num))}
                  </div>
                ))}
            </div>
          </>
        ) : (
          <span className={classes.status}>{gameOverText || statusText}</span>
        )}
        <Button
          className={`${globalClasses.button} ${classes.quit} ${collectPoint && classes.win}`}
          onClick={() => handleExit(gameOver ? 'exit' : 'quit', closeModal)}>
          {collectPoint ? 'Collect 1 point!' : 'Exit'}
        </Button>
      </Box>
    </Modal>
  );
};

export default TicTacToeModal;
